import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useMenu } from '../../context/MenuContext';
import PhotoUpload from './PhotoUpload';
import PhotoGalleryModal from './PhotoGalleryModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiImage, FiDollarSign, FiCalendar, FiCheck, FiX, FiBookmark, FiCopy, FiExternalLink } = FiIcons;

const MenuFormWithUpload = ({ 
  initialData = {}, 
  onSubmit, 
  onCancel, 
  isLoading = false, 
  isEditing = false, 
  allowDayEdit = false 
}) => {
  const { availableDays } = useMenu();
  const [formData, setFormData] = useState({
    day: availableDays[0] || 'dienstag',
    name: '',
    description: '',
    image: '',
    price: 18.50,
    ...initialData
  });
  const [errors, setErrors] = useState({});
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);

  // Update form when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        day: initialData.day || availableDays[0] || 'dienstag',
        name: initialData.name || '',
        description: initialData.description || '',
        image: initialData.image || '',
        price: initialData.price || 18.50,
      });
    }
  }, [initialData, availableDays]);

  // Templates laden
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const { data, error } = await supabase
          .from('menu_templates_a7b9c8d6e5')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTemplates(data || []);
      } catch (error) {
        console.error('Fehler beim Laden der Templates:', error);
      }
    };

    loadTemplates();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Beschreibung ist erforderlich';
    }

    if (!formData.image.trim()) {
      newErrors.image = 'Bild URL ist erforderlich';
    } else if (!formData.image.match(/^https?:\/\/.+/)) {
      newErrors.image = 'Bild URL muss mit http:// oder https:// beginnen';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Preis muss größer als 0 sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      name: template.name,
      description: template.description,
      image: template.image,
      price: template.price
    }));
    setShowTemplates(false);
    setErrors({});
  };

  const saveAsTemplate = async () => {
    if (!formData.name || !formData.description || !formData.image) {
      alert('Bitte füllen Sie alle Felder aus, bevor Sie ein Template erstellen.');
      return;
    }

    try {
      const { error } = await supabase
        .from('menu_templates_a7b9c8d6e5')
        .insert({
          name: formData.name,
          description: formData.description,
          image: formData.image,
          price: formData.price,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      alert('Template erfolgreich gespeichert!');

      // Templates neu laden
      const { data } = await supabase
        .from('menu_templates_a7b9c8d6e5')
        .select('*')
        .order('created_at', { ascending: false });

      setTemplates(data || []);
    } catch (error) {
      console.error('Fehler beim Speichern des Templates:', error);
      alert('Fehler beim Speichern des Templates.');
    }
  };

  // Handle photo selection from upload or gallery
  const handlePhotoSelect = (url) => {
    setFormData(prev => ({
      ...prev,
      image: url
    }));
    
    // Clear image error if it exists
    if (errors.image) {
      setErrors(prev => ({
        ...prev,
        image: ''
      }));
    }
  };

  // Get day options dynamically based on available days
  const getDayOptions = () => {
    const dayLabels = {
      'montag': 'Montag',
      'dienstag': 'Dienstag',
      'mittwoch': 'Mittwoch',
      'donnerstag': 'Donnerstag',
      'freitag': 'Freitag',
      'samstag': 'Samstag',
      'sonntag': 'Sonntag'
    };

    return availableDays.map(day => ({
      value: day,
      label: dayLabels[day] || day.charAt(0).toUpperCase() + day.slice(1)
    }));
  };

  const dayOptions = getDayOptions();

  return (
    <div>
      {/* Template Actions */}
      <div className="mb-6 flex flex-wrap gap-3">
        <motion.button
          type="button"
          onClick={() => setShowTemplates(!showTemplates)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors"
        >
          <SafeIcon icon={FiBookmark} />
          <span>Templates ({templates.length})</span>
        </motion.button>

        <motion.button
          type="button"
          onClick={saveAsTemplate}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg hover:bg-green-200 transition-colors"
        >
          <SafeIcon icon={FiBookmark} />
          <span>Als Template speichern</span>
        </motion.button>
      </div>

      {/* Templates Grid */}
      {showTemplates && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Gespeicherte Templates</h3>
          {templates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white p-4 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors cursor-pointer"
                  onClick={() => applyTemplate(template)}
                >
                  <div className="flex items-center space-x-3">
                    <img
                      src={template.image}
                      alt={template.name}
                      className="w-16 h-12 object-cover rounded"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/64x48?text=Bild';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{template.name}</h4>
                      <p className="text-sm text-gray-500 truncate">{template.description}</p>
                      <p className="text-sm font-medium text-primary-600">CHF {template.price.toFixed(2)}</p>
                    </div>
                    <SafeIcon icon={FiCopy} className="text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Keine Templates gefunden</p>
          )}
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Day selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tag *
          </label>
          <div className="relative">
            <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              name="day"
              value={formData.day}
              onChange={handleChange}
              disabled={isEditing && !allowDayEdit}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                errors.day ? 'border-red-500' : 'border-gray-300'
              } ${isEditing && !allowDayEdit ? 'bg-gray-100' : ''}`}
            >
              {dayOptions.map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
          {errors.day && (
            <p className="mt-1 text-sm text-red-600">{errors.day}</p>
          )}
          {dayOptions.length === 0 && (
            <p className="mt-1 text-sm text-amber-600">
              ⚠️ Keine verfügbaren Tage konfiguriert. Bitte konfigurieren Sie zuerst die verfügbaren Tage.
            </p>
          )}
        </div>

        {/* Menu name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Menüname *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="z.B. Schweinemedaillons mit Kräuterbutter"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Beschreibung *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Detaillierte Beschreibung des Menüs"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
        </div>

        {/* Image Section - Enhanced with Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Menü-Foto *
          </label>
          
          {/* Image URL Input */}
          <div className="relative mb-4">
            <SafeIcon icon={FiImage} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="image"
              value={formData.image}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                errors.image ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://beispiel.com/bild.jpg oder laden Sie ein Foto hoch"
            />
          </div>

          {/* Photo Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-4">
            <motion.button
              type="button"
              onClick={() => setShowUploadSection(!showUploadSection)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-lg hover:bg-primary-200 transition-colors"
            >
              <SafeIcon icon={FiImage} />
              <span>Foto hochladen</span>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => setShowGalleryModal(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <SafeIcon icon={FiExternalLink} />
              <span>Aus Galerie wählen</span>
            </motion.button>
          </div>

          {errors.image && (
            <p className="mb-4 text-sm text-red-600">{errors.image}</p>
          )}

          {/* Photo Upload Section */}
          {showUploadSection && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-4"
            >
              <PhotoUpload 
                onPhotoSelect={handlePhotoSelect}
                selectedPhoto={formData.image}
                showGallery={false}
              />
            </motion.div>
          )}

          {/* Image Preview */}
          {formData.image && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Vorschau:</p>
              <div className="relative inline-block">
                <img
                  src={formData.image}
                  alt="Vorschau"
                  className="w-64 h-48 object-cover rounded-lg border border-gray-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/300x200?text=Bild+nicht+verfügbar';
                  }}
                />
                {formData.image.includes('supabase.co') && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                    ✓ Hochgeladen
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preis (CHF) *
          </label>
          <div className="relative">
            <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.10"
              min="0"
              className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                errors.price ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="18.50"
            />
          </div>
          {errors.price && (
            <p className="mt-1 text-sm text-red-600">{errors.price}</p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex space-x-4">
          <motion.button
            type="submit"
            disabled={isLoading || dayOptions.length === 0}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex-1 flex items-center justify-center space-x-2 bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 ${
              isLoading || dayOptions.length === 0 ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <span>{isEditing ? 'Aktualisieren' : 'Erstellen'}</span>
            <SafeIcon icon={FiCheck} />
          </motion.button>

          <motion.button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors duration-300"
          >
            <span>Abbrechen</span>
            <SafeIcon icon={FiX} />
          </motion.button>
        </div>
      </form>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        onPhotoSelect={handlePhotoSelect}
        selectedPhoto={formData.image}
      />
    </div>
  );
};

export default MenuFormWithUpload;